/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { createConversationPhraseListStore } from 'src/luis/core/stores/conversationPhraseListStore';
import { getPhraseListService } from 'src/luis/modules/features/services/phraseListService';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';
import { PhraseListSwagger } from 'src/luis/modules/features/types/PhraseListSwagger';
import { deleteDanglingFeaturePointer } from 'src/luis/modules/features/utils/deleteDanglingFeaturePointer';
import { makePhraseListGlobalUpdates } from 'src/luis/modules/features/utils/makePhraselistGlobalUpdates';
import { loadIntentsHelper } from 'src/luis/modules/intents/intentHandlerGroup';
import { loadEntitiesHelper } from 'src/luis/projects/entities/utils/loadCommonEntitiesHelper';
import { PhraseListStore } from 'src/luis/types/stores/features/PhraseListStore';
import { VersionedAppId } from 'src/luis/types/VersionedAppId';

export type PhraseListHandlerGroup = ReturnType<typeof createPhraseListHandlerGroup>;

/**
 * Creates a phrase list handler group for phrase list
 * related operations.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The dependencies needed for the handler group.
 */
export const createPhraseListHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const getAppVersionStore = ({ appId, versionId }: VersionedAppId) =>
        rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);

    /**
     * Loads the phrase lists for the given application and version.
     */
    const loadPhraseLists = async (params?: HandlerParams) => {
        const { context } = params;
        const { appId, versionId } = context;

        const phraseListService = getPhraseListService({ context });
        const { phraseListStoreHouse } = getAppVersionStore({ appId, versionId });

        if (!context.forceRefresh && (phraseListStoreHouse.isLoading || phraseListStoreHouse.isLoaded)) {
            return;
        }

        phraseListStoreHouse.setIsLoading(true);

        const phraseListStores = await phraseListService.getPhraseLists();

        phraseListStoreHouse.setIsLoading(false);
        phraseListStoreHouse.setItems(phraseListStores);
        phraseListStoreHouse.setIsLoaded(true);
    };

    /**
     * Creates a new phrase list.
     *
     * @param param0.dto The data needed to create a phrase list.
     */
    const createPhraseList = async ({ context, phraseList, tracker }: HandlerParams<{ phraseList: PhraseList }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const phraseListService = getPhraseListService({ context });
            const appVersionStore = getAppVersionStore({ appId, versionId });
            const {
                entityStoreHouse: { items: entityStores },
                intentStoreHouse: { items: intentStores }
            } = appVersionStore;
            const phraseListStore = await phraseListService.createPhraseList(phraseList);

            appVersionStore.phraseListStoreHouse.add(phraseListStore);

            if (phraseListStore.isGlobal) {
                makePhraseListGlobalUpdates({ entityStores, intentStores, phraseList: phraseListStore });
            }

            if (tracker) {
                tracker.setContext(phraseListStore);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Updates the phrase list with the given data.
     *
     * @param param0.dto The dto containing the needed information.
     */
    const updatePhraseList = async ({ context, phraseList, tracker }: HandlerParams<{ phraseList: PhraseList }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const phraseListService = getPhraseListService({ context });
            const appVersionStore = getAppVersionStore({ appId, versionId });
            const oldPhraseList = appVersionStore.phraseListStoreHouse.findById(phraseList.id);

            await phraseListService.updatePhraseList(<PhraseListSwagger>{
                ...phraseList,
                enabledForAllModels: phraseList.isGlobal,
                phrases: phraseList.phrases.toString()
            });

            const phraseListStore = createConversationPhraseListStore(phraseList.id, phraseList);
            appVersionStore.phraseListStoreHouse.replace(phraseListStore);

            if (phraseList.isGlobal !== oldPhraseList.isGlobal) {
                await loadEntitiesHelper(rootStore)({ context: { ...context, forceRefresh: true } });
                await loadIntentsHelper(rootStore)({ context: { ...context, forceRefresh: true } });
            }

            if (tracker) {
                tracker.setContext(phraseListStore);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes the phrase list with the given id.
     *
     * @param param0.id The phrase list id to delete.
     */
    const deletePhraseList = async ({ context, id }: HandlerParams<Pick<PhraseListStore, 'id'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const phraseListService = getPhraseListService({ context });
            const {
                phraseListStoreHouse,
                intentStoreHouse: { items: intentStores },
                entityStoreHouse: { items: entityStores }
            } = getAppVersionStore({ appId, versionId });

            await phraseListService.deletePhraseList({ id });

            // Deletes the dangling pointers for the models that uses these entities as a feature
            deleteDanglingFeaturePointer({ referencedFeatureId: id, intentStores, entityStores });

            phraseListStoreHouse.removeById(id);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return {
        loadPhraseLists,
        createPhraseList,
        updatePhraseList,
        deletePhraseList
    };
};
